import {
    addSortToUrl,
    cleanEntityString,
    processFilters,
    processGetManyByReferenceFilter,
    processGetManyFilter, refuseOnHttpError} from "./provider/defaults";
import {
    CreateParams,
    CreateResult,
    DataProvider,
    DeleteManyParams,
    DeleteManyResult,
    DeleteParams,
    DeleteResult,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyReferenceParams,
    GetManyReferenceResult,
    GetManyResult,
    GetOneParams,
    GetOneResult,
    UpdateManyParams,
    UpdateManyResult,
    UpdateParams,
    UpdateResult
} from "react-admin";
import { read_cookie } from 'sfcookies';
import { apiUrl, UseUrlManagerCleanUrl } from "../components/utils/url-manager";
import { CustomerActivityMonitor } from "../types/entities";

/**
 * The customer activity trail. **Read only, by construction rather than by convention:** the
 * API resource exposes Get and GetCollection and nothing else, so the four write methods below
 * reject rather than being wired to endpoints that do not exist.
 *
 * Unlike its siblings, this provider **checks the HTTP status** instead of ignoring it. Every
 * other provider in this directory has a commented-out status block and then falls through to
 * `response.json()`, so a 401 renders as a successful empty list (HQ-06). New code should not
 * add to that, and an audit trail that silently renders empty is worse than most.
 */
function cleanParams(data: any): CustomerActivityMonitor {
    return {
        id: data['id'],
        customerAccount: cleanEntityString(data['customerAccount'], '/hq_api/customer_accounts/'),
        actor: data['actor'] ?? null,
        actorName: data['actorName'],
        actorEmail: data['actorEmail'] ?? null,
        actorRole: data['actorRole'] ?? null,
        actorRoleLabel: data['actorRoleLabel'] ?? null,
        category: data['category'],
        type: data['type'],
        typeLabel: data['typeLabel'],
        subjectType: data['subjectType'] ?? null,
        subjectId: data['subjectId'] ?? null,
        subjectLabel: data['subjectLabel'] ?? null,
        changes: data['changes'] ?? null,
        context: data['context'] ?? null,
        changeSet: data['changeSet'] ?? [],
        contextEntries: data['contextEntries'] ?? [],
        createdAt: data['createdAt'],
    };
}

function collectionFrom(data: any): { data: CustomerActivityMonitor[], total: number } {
    const responseData: { data: CustomerActivityMonitor[], total: number } = { data: [], total: 0 };
    if (Array.isArray(data['hydra:member'])) {
        const members: any[] = data['hydra:member'];
        for (let i: number = 0; i < members.length; i++) {
            responseData.data.push(cleanParams(members[i]));
        }
        responseData.total = data['hydra:totalItems'];
    }

    return responseData;
}

function readHeaders(): Headers {
    return new Headers({
        'Content-Type': 'application/json',
        'token': read_cookie('auth'),
        'hq': 'hq'
    });
}

const appendOnly: (action: string) => Promise<never> = (action: string): Promise<never> =>
    Promise.reject({
        message: `Activity records cannot be ${ action }: the trail is append-only.`,
        status: 405,
    });

const customerActivityMonitorDataProvider: DataProvider = {

    getList: async (resource: string, params: GetListParams): Promise<GetListResult> => {
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processFilters(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });

        return fetch(new Request(url, { method: 'GET', headers: readHeaders() }))
            .then(refuseOnHttpError)
            .then((response: Response): Promise<any> => response.json())
            .then(collectionFrom);
    },

    getOne: async (resource: string, params: GetOneParams): Promise<GetOneResult> => {
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url: url });

        return fetch(new Request(url, { method: 'GET', headers: readHeaders() }))
            .then(refuseOnHttpError)
            .then((response: Response): Promise<any> => response.json())
            .then((data: any): { data: CustomerActivityMonitor } => ({ data: cleanParams(data) }));
    },

    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        let url: string = apiUrl + resource;
        url = processGetManyFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });

        return fetch(new Request(url, { method: 'GET', headers: readHeaders() }))
            .then(refuseOnHttpError)
            .then((response: Response): Promise<any> => response.json())
            .then(collectionFrom);
    },

    getManyReference: async (resource: string, params: GetManyReferenceParams): Promise<GetManyReferenceResult> => {
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processGetManyByReferenceFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });

        return fetch(new Request(url, { method: 'GET', headers: readHeaders() }))
            .then(refuseOnHttpError)
            .then((response: Response): Promise<any> => response.json())
            .then(collectionFrom);
    },

    create: async (_resource: string, _params: CreateParams): Promise<CreateResult> => appendOnly('created by hand'),
    update: async (_resource: string, _params: UpdateParams): Promise<UpdateResult> => appendOnly('edited'),
    updateMany: async (_resource: string, _params: UpdateManyParams): Promise<UpdateManyResult> => appendOnly('edited'),
    delete: async (_resource: string, _params: DeleteParams): Promise<DeleteResult> => appendOnly('deleted'),
    deleteMany: async (_resource: string, _params: DeleteManyParams): Promise<DeleteManyResult> => appendOnly('deleted'),
};

export default customerActivityMonitorDataProvider;
